import { Flex, styled } from '@kandji-inc/nectar-ui';
import { lazy, useCallback, useEffect, useMemo, useState } from 'react';
import { Pagination } from 'src/components/ui';
import { i18n } from 'src/i18n';
import ThreatListErrorBoundary from '../../common/components/ThreatListErrorBoundary';
import ThreatListLoader from '../../common/components/ThreatListLoader';
import ThreatListSuspense from '../../common/components/ThreatListSuspense';
import ThreatListEmptyStates from '../../common/components/ThreatListTable/ThreatListEmptyStates';
import constants from '../../common/constants';
import useStateParams from '../../common/hooks/use-state-params';
import convertToCSV from '../../common/utils/convert-to-csv';
import downloadCSVFile from '../../common/utils/download-csv-file';
import getDisplayDetectionPeriod from '../../common/utils/get-display-detection-period';
import type {
  DetectionTypeSelectorState,
  FiltersState,
  GetBehavioralThreatGroupResponse,
  GetByThreatsResponse,
  MitreTechnique,
  PageSize,
  PaginationState,
  ShowToast,
  SortColumnState,
  ThreatGroupUnion,
} from '../../threat.types';
import ThreatListFilters from '../ThreatListFilters';
import ThreatListTable from './ThreatListTable/ThreatListTable';
import useExportThreatsMutation from './hooks/use-export-threats';
import useGetBehavioralThreatsGroup from './hooks/use-get-behavioral-threats-group/use-get-behavioral-threats-group';
import useGetByThreats from './hooks/use-get-by-threats/use-get-by-threats';
import useGetMitreTechniques from './hooks/use-get-mitre-techniques/use-get-mitre-techniques';
import {
  createPaginationParamConfig,
  defaultFilters,
  filtersParamConfig,
  sortParamConfig,
} from './params/params';

const ThreatListSidePanel = lazy(
  () => import('./SidePanels/ThreatListSidePanel/ThreatListSidePanel'),
);

const ThreatListSidePanelPM = lazy(
  () => import('./SidePanels/ThreatListSidePanelPM/ThreatListSidePanel'),
);

const Container = styled(Flex, {
  position: 'relative',
  height: 'calc(100vh - 80px)',
  flexDirection: 'column',
});

const TableEmptyStateContainer = styled(Flex, {
  backgroundColor: 'rgba(255,255,255,0.85)',
  height: 'calc(100% - 188px)',
  position: 'absolute',
  width: '100%',
  top: 128,
  minHeight: 250,
});

const PaginationContainer = styled(Flex, {
  boxShadow:
    '$shadows$inset_border_bottom_1 $colors$neutral0, $shadows$inset_border_top_1 $colors$neutral30',
  padding: '$3 $5',
  backgroundColor: '$neutral0',
  zIndex: 3,
});

const errorBoundaryTableCss = {
  flex: 1,
  minHeight: 400,
};

type ThreatListByThreatsProps = {
  showToast: ShowToast;
};

const ThreatListByThreats = (props: ThreatListByThreatsProps) => {
  const { showToast } = props;
  const [selectedForSidePanel, setSelectedForSidePanel] =
    useState<ThreatGroupUnion>();

  const { paginationConfig, updateStoredPageSize } =
    createPaginationParamConfig('threatListGroupsPaginationPageSize');

  const {
    filters,
    setFilters,
    pagination,
    setPagination,
    sort,
    setSort,
    isDefaultFilterState,
  } = useStateParams<FiltersState, PaginationState, SortColumnState>(
    filtersParamConfig,
    paginationConfig,
    sortParamConfig,
    defaultFilters,
  );

  const isFileDetectionType =
    filters.detectionType === constants.THREAT_DETECTION_TYPE.FILE ||
    filters.detectionType === undefined;

  const {
    data: dataFileThreats,
    isLoading: isLoadingFileThreats,
    isFetching: isFetchingFileThreats,
    isError: isErrorFetchingFileThreats,
    error: errorFetchingFileThreats,
    refetch: refetchThreats,
  } = useGetByThreats(filters, sort, pagination, isFileDetectionType);

  useEffect(() => {
    if (!isFetchingFileThreats && selectedForSidePanel) {
      const updatedThreat = threats.find(
        (threat) => threat.threat_id === selectedForSidePanel.threat_id,
      );
      setSelectedForSidePanel(updatedThreat);
    }
  }, [isFetchingFileThreats]);

  const {
    data: dataBehavioralThreats,
    isLoading: isLoadingBehavioralThreats,
    isError: isErrorFetchingBehavioralThreats,
    error: errorFetchingBehavioralThreats,
    refetch: refetchBehavioralThreats,
  } = useGetBehavioralThreatsGroup(
    filters,
    sort,
    pagination,
    !isFileDetectionType,
  );

  const { data: dataMitreTechniques, isLoading: isLoadingMitreTechniques } =
    useGetMitreTechniques();

  const mitre: MitreTechnique[] = dataMitreTechniques || [];

  const {
    data: dataThreats,
    isLoading: isLoadingThreats,
    isError: isErrorFetchingThreats,
    error: errorFetchingThreats,
    refetch,
  } = useMemo(
    () =>
      isFileDetectionType
        ? {
            data: dataFileThreats,
            isLoading: isLoadingFileThreats,
            isError: isErrorFetchingFileThreats,
            error: errorFetchingFileThreats,
            refetch: refetchThreats,
          }
        : {
            data: dataBehavioralThreats,
            isLoading: isLoadingBehavioralThreats,
            isError: isErrorFetchingBehavioralThreats,
            error: errorFetchingBehavioralThreats,
            refetch: refetchBehavioralThreats,
          },
    [
      isFileDetectionType,
      dataFileThreats,
      isLoadingFileThreats,
      isErrorFetchingFileThreats,
      errorFetchingFileThreats,
      dataBehavioralThreats,
      isLoadingBehavioralThreats,
      isErrorFetchingBehavioralThreats,
      errorFetchingBehavioralThreats,
      refetchThreats,
      refetchBehavioralThreats,
    ],
  );

  const { fetchForExport, isLoading: isExporting } = useExportThreatsMutation(
    /* istanbul ingore next */
    (data: GetByThreatsResponse | GetBehavioralThreatGroupResponse) => {
      if (!data?.data) return;

      downloadCSVFile(convertToCSV(data.data));
      showToast(`export-complete`);
    },
    () => showToast(`export-error`),
  );

  const dataCount = dataThreats?.count;
  const threats = (dataThreats?.data || []) as ThreatGroupUnion[];
  const isEmpty = dataCount === 0;
  const isFiltering = !isDefaultFilterState(['detectionType']);
  const isFilteringOmittingFields = !isDefaultFilterState([
    'detectionDate',
    'detectionType',
  ]);
  const displayDetectionPeriod: string = getDisplayDetectionPeriod(
    filters.detectionDate,
  );

  const setFiltersAndReset = useCallback(
    (newFilters: FiltersState) => {
      setFilters(newFilters);
      setPagination({ page: 1 });
    },
    [setFilters, setPagination],
  );

  const handleClearFilters = useCallback(
    (filterName: string) => {
      setFiltersAndReset({ [filterName]: undefined });
    },
    [setFiltersAndReset],
  );

  const handleApplyFilters = (filterName: string, value: unknown) => {
    setFiltersAndReset({ [filterName]: value });
  };

  const handleApplyFiltersForDetectionType = (
    value: DetectionTypeSelectorState | undefined,
  ) => {
    setFiltersAndReset({
      status: undefined,
      classification: undefined,
      mitre: undefined,
      detectionType: value,
    });
  };

  const handleClearAllFilters = () => {
    setFiltersAndReset({
      status: undefined,
      classification: undefined,
      detectionDate: undefined,
      mitre: undefined,
      query: undefined,
    });
  };

  // istanbul ignore next
  const handlePaginationChange = (page: number, pageSize: PageSize) => {
    setPagination({ page, pageSize });
    updateStoredPageSize(pageSize);
  };

  const handleColumnSortChange = useCallback(
    /*istanbul ignore next */ (sortState: SortColumnState) => {
      setSort(sortState);
    },
    [setSort],
  );

  const handleExport = /* istanbul ignore next */ () => {
    showToast(`export-preparing`);
    fetchForExport({
      filters,
      sort,
      pagination: {},
      isFileDetectionType,
      isAll: true,
    });
  };

  const handleOpenSidePanel = (threat: ThreatGroupUnion) => {
    setSelectedForSidePanel(threat);
  };

  const handleCloseSidePanel = () => {
    setSelectedForSidePanel(undefined);
  };

  const ThreatListSidePanelPlaceholder = isFileDetectionType
    ? ThreatListSidePanel
    : ThreatListSidePanelPM;

  return (
    <ThreatListErrorBoundary css={errorBoundaryTableCss} preReset={refetch}>
      <Container>
        <ThreatListFilters
          filters={filters}
          mitre={mitre}
          isMitreLoading={isLoadingMitreTechniques}
          onApply={handleApplyFilters}
          onDetectionTypeApply={handleApplyFiltersForDetectionType}
          onClear={handleClearFilters}
          onClearAll={handleClearAllFilters}
          isFiltering={isFiltering}
          isExportHidden={isEmpty || isLoadingThreats}
          isExportDisabled={isExporting}
          onExport={handleExport}
          isFileDetectionType={isFileDetectionType}
        />

        <ThreatListTable
          isFileDetectionType={isFileDetectionType}
          searchTerm={filters.query}
          threats={threats}
          sort={sort}
          onColumnSortChange={handleColumnSortChange}
          onOpenSidePanel={handleOpenSidePanel}
          selectedThreat={selectedForSidePanel}
          isError={isErrorFetchingThreats}
          error={errorFetchingThreats}
          isEmpty={isEmpty}
        />

        {(isEmpty || isLoadingThreats) && (
          <TableEmptyStateContainer>
            {!isLoadingThreats ? (
              <ThreatListEmptyStates
                isFiltering={isFilteringOmittingFields}
                displayDetectionPeriod={displayDetectionPeriod}
                displayDetectionSubject={
                  isFileDetectionType
                    ? i18n.t('malware or PUP detections')
                    : i18n.t('malicious or suspicious behavioral detections')
                }
              />
            ) : (
              <ThreatListLoader label={i18n.t('Loading Threats')} />
            )}
          </TableEmptyStateContainer>
        )}

        <PaginationContainer>
          <Pagination
            currentPage={pagination.page}
            totalItems={dataCount || 0}
            itemsPerPage={pagination.pageSize}
            onPageChange={
              /* istanbul ignore next */ (page: number) =>
                handlePaginationChange(page, pagination.pageSize!)
            }
            onItemsPerPageChange={
              /* istanbul ignore next */ (itemsPerPage) =>
                handlePaginationChange(1, itemsPerPage as PageSize)
            }
          />
        </PaginationContainer>
      </Container>

      {selectedForSidePanel && (
        <ThreatListSuspense>
          <ThreatListSidePanelPlaceholder
            threat={selectedForSidePanel}
            detectionDate={filters.detectionDate}
            onClose={handleCloseSidePanel}
            tableSearchQuery={filters.query}
            refetchThreats={refetch}
          />
        </ThreatListSuspense>
      )}
    </ThreatListErrorBoundary>
  );
};

export default ThreatListByThreats;
